import React from 'react'
import Dropzone from './previsional/Dropzone'
import ConfirmModal from './ConfirmModal'
import Tooltip from './Tooltip'
import { FIELD_TOOLTIPS } from '../utils/fieldTooltips'

export default function HistorialModeSelector({
  modoHerramienta, cargaHistorialModo, setCargaHistorialModo,
  simulationEnabled, rows,
  selectedCaseId, setSelectedCaseId, simulationCases, loadSimulationCase,
  caseData, resetRemainingSession, setUltimoPeriodoDetectado,
  extractPdfHeaderCese, setFechaCeseCabeceraPdf,
  toFechaInputFromPeriodoKey, expediente, setExpediente,
  resetCaseSession, setFechaCeseSugerida,
  setActividadFuentes, titularCuilOrigen, setTitularCuilOrigen,
  processParserOutput, activeRules, normalizeReviewRows,
  setManualResumenTotalesDraft, setRows, setRawRows, setCalculoReady,
  setProgressLabel, progressLabel,
  manualRowsDraft, updateManualRow, addManualRow, manualRowsValid, loadManualRows,
  sanitizeExpedienteScenarioForPdfIngestion,
  asistidoMode,
}) {
  const [showCaseChangeConfirm, setShowCaseChangeConfirm] = React.useState(null)

  const handleProcessed = (d) => {
    caseData.resetCoreCaseSession()
    resetRemainingSession()
    const ultimo = d?.ultimoPeriodoDetectado || ''
    setUltimoPeriodoDetectado(ultimo)
    const ceseCabecera = extractPdfHeaderCese(d)
    setFechaCeseCabeceraPdf(ceseCabecera)
    const sugeridaKey = ceseCabecera || ultimo || ''
    const sugeridaInput = toFechaInputFromPeriodoKey(sugeridaKey)
    const currentCuil = String(expediente?.cuilCuit || '').replace(/\D/g, '')
    const currentCaseKey = `${currentCuil || 'sin-cuit'}-${String(expediente?.fechaCese || '').slice(0, 10) || 'sin-cese'}`
    const effectiveIncomingCuil = String(d?.cuil || '').replace(/\D/g, '') || String(expediente?.cuilCuit || '').replace(/\D/g, '')
    const incomingCaseKey = `${effectiveIncomingCuil || currentCuil || 'sin-cuit'}-${sugeridaInput || String(expediente?.fechaCese || '').slice(0, 10) || 'sin-cese'}`
    if (rows.length > 0 && incomingCaseKey !== currentCaseKey) {
      setShowCaseChangeConfirm({ incomingCaseKey, currentCaseKey, efectiveIncomingCuil: effectiveIncomingCuil, sugeridaInput, d })
      return
    }
    applyProcessedData(d)
  }

  const applyProcessedData = (d, resetSession) => {
    const ultimo = d?.ultimoPeriodoDetectado || ''
    setUltimoPeriodoDetectado(ultimo)
    const ceseCabecera = extractPdfHeaderCese(d)
    setFechaCeseCabeceraPdf(ceseCabecera)
    const sugeridaKey = ceseCabecera || ultimo || ''
    const sugeridaInput = toFechaInputFromPeriodoKey(sugeridaKey)
    const effectiveIncomingCuil = String(d?.cuil || '').replace(/\D/g, '') || String(expediente?.cuilCuit || '').replace(/\D/g, '')
    if (resetSession) {
      resetCaseSession({
        preservePersonals: true,
        reason: `Sesión técnica reiniciada por cambio de caso detectado en PDF (${showCaseChangeConfirm?.currentCaseKey} -> ${showCaseChangeConfirm?.incomingCaseKey}).`,
      })
    }
    setFechaCeseSugerida(sugeridaInput)
    setExpediente((p) => {
      const saneado = sanitizeExpedienteScenarioForPdfIngestion(p)
      if (String(saneado?.fechaCese || '').trim()) return saneado
      return sugeridaInput ? { ...saneado, fechaCese: sugeridaInput } : saneado
    })
    setActividadFuentes({
      autonomos: d?.historico_autonomos || d?.historicoAutonomos || d?.debug?.historico_autonomos || [],
      desempleo: d?.historico_desempleo || d?.historicoDesempleo || d?.desempleo || d?.debug?.historico_desempleo || [],
    })
    if (!titularCuilOrigen && String(d?.cuil || '').trim()) {
      setTitularCuilOrigen(String(d.cuil).replace(/\D/g, ''))
    }
    const { normalized, normalizedPre94, draftRows } = processParserOutput(d, {
      rows, expediente, activeRules,
      incomingCuil: effectiveIncomingCuil,
    })
    setRawRows(normalizeReviewRows(d?.remuneraciones || []))
    setManualResumenTotalesDraft(draftRows.length ? draftRows : [{ cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
    setRows(normalized)
    setCalculoReady(false)
    setShowCaseChangeConfirm(null)
  }

  return (
    <section className="rounded-xl border border-fuchsia-300/40 bg-fuchsia-500/10 p-4">
      <p className="mb-1 text-xs uppercase tracking-wide text-fuchsia-100">Tarjeta 4 - Carga de Historial Laboral</p>
      <p className="mb-3 text-xs text-fuchsia-50/90">Elegí cómo querés cargar la historia laboral: PDF ANSES o carga manual.</p>
      {modoHerramienta === 'base' && (
        <div className="panel-profile-ok mb-3 rounded-lg p-2 text-xs text-profile-ok">
          Flujo recomendado: 1) cargar historial, 2) corregir filas marcadas, 3) generar cálculo, 4) emitir Informe Base.
        </div>
      )}
      <div className="mb-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => setCargaHistorialModo('pdf')} className={`rounded-xl px-4 py-2 text-sm font-semibold ${cargaHistorialModo === 'pdf' ? 'panel-profile-accent text-profile-accent' : 'panel-profile'}`}>Cargar por PDF</button>
        {modoHerramienta === 'avanzado' && (
          <button type="button" onClick={() => setCargaHistorialModo('manual')} className={`rounded-xl px-4 py-2 text-sm font-semibold ${cargaHistorialModo === 'manual' ? 'panel-profile-accent text-profile-accent' : 'panel-profile'}`}>Carga Manual</button>
        )}
        {modoHerramienta === 'avanzado' && simulationEnabled && (
          <button type="button" onClick={() => setCargaHistorialModo('simulation')} className={`rounded-xl px-4 py-2 text-sm font-semibold ${cargaHistorialModo === 'simulation' ? 'panel-profile-accent text-profile-accent' : 'panel-profile'}`}>Simulacion (Etapas 2/3)</button>
        )}
      </div>

      {simulationEnabled && cargaHistorialModo === 'simulation' && rows.length === 0 && (
        <div className="mt-2 rounded-xl border border-white/20 bg-slate-900/50 p-3">
          <p className="mb-2 text-xs uppercase tracking-wide text-slate-300">Modo simulacion habilitado</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-300">
              Caso de prueba
              <select value={selectedCaseId} onChange={(e) => setSelectedCaseId(e.target.value)} className="mt-1 h-9 min-w-[280px] rounded border border-white/25 bg-slate-900/60 px-2 text-white">
                {simulationCases.map((item) => (
                  <option key={item.meta.caseId} value={item.meta.caseId}>{item.meta.caseId} - {item.meta.label}</option>
                ))}
              </select>
            </label>
            <button type="button" onClick={loadSimulationCase} className="btn-profile-soft h-9 rounded-lg px-3 text-xs font-semibold">
              Cargar caso simulado
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Bypass activo: se omite la etapa PDF y se inyectan datos sinteticos para calculo base y avanzado.</p>
        </div>
      )}

      {cargaHistorialModo === 'pdf' && rows.length === 0 && (
        <div id="tour-upload" className="mt-2">
          <Dropzone
            parserProfile="docai_universal"
            onProcessed={handleProcessed}
            onProgress={setProgressLabel}
          />
          {progressLabel && <p className="mt-2 text-xs text-slate-300">{progressLabel}</p>}
        </div>
      )}

      {cargaHistorialModo === 'manual' && rows.length === 0 && (
        <div className={`mt-2 overflow-x-auto rounded-xl border ${asistidoMode ? 'bg-emerald-900/20 border-emerald-300/30 p-3' : 'border-white/20 bg-slate-900/50 p-3'}`}>
          <table className="min-w-full text-sm text-slate-100">
            <thead>
              <tr className="text-left text-xs text-slate-300">
                <th className="px-2 py-1">{asistidoMode ? <Tooltip text={FIELD_TOOLTIPS.periodo.tooltipText}>Periodo</Tooltip> : 'Periodo'}</th>
                <th className="px-2 py-1">{asistidoMode ? <Tooltip text={FIELD_TOOLTIPS.empresa.tooltipText}>Empresa</Tooltip> : 'Empresa'}</th>
                <th className="px-2 py-1">{asistidoMode ? <Tooltip text={FIELD_TOOLTIPS.cuit.tooltipText}>CUIT</Tooltip> : 'CUIT'}</th>
                <th className="px-2 py-1">{asistidoMode ? <Tooltip text={FIELD_TOOLTIPS.montoBruto.tooltipText}>Monto</Tooltip> : 'Monto'}</th>
                {!asistidoMode && <th className="px-2 py-1">Estrategia</th>}
              </tr>
            </thead>
            <tbody>
              {manualRowsDraft.map((r, i) => (
                <tr key={`manual-${i}`}>
                  <td className="px-2 py-1"><input type="month" value={r.periodo} onChange={(e) => updateManualRow(i, 'periodo', e.target.value)} className="h-9 w-40 rounded border border-white/25 bg-slate-900/60 px-2 text-white" /></td>
                  <td className="px-2 py-1"><input type="text" value={r.empresa} onChange={(e) => updateManualRow(i, 'empresa', e.target.value)} className="h-9 w-44 rounded border border-white/25 bg-slate-900/60 px-2 text-white" /></td>
                  <td className="px-2 py-1"><input type="text" value={r.cuit} onChange={(e) => updateManualRow(i, 'cuit', e.target.value.replace(/\D/g, ''))} className="h-9 w-36 rounded border border-white/25 bg-slate-900/60 px-2 text-white" /></td>
                  <td className="px-2 py-1"><input type="text" inputMode="decimal" value={r.monto} onChange={(e) => updateManualRow(i, 'monto', e.target.value)} className="h-9 w-36 rounded border border-white/25 bg-slate-900/60 px-2 text-white" /></td>
                  {!asistidoMode && (
                    <td className="px-2 py-1">
                      {String(r.monto || '').replace(',', '.').replace(/[^0-9.]/g, '') === '' || Number(String(r.monto || '').replace(',', '.')) <= 0 ? (
                        <select value={r.estrategia || 'EXCLUIR_DIVISOR'} onChange={(e) => updateManualRow(i, 'estrategia', e.target.value)} className="h-9 rounded border border-white/25 bg-slate-900/60 px-2 text-xs text-white">
                          <option value="EXCLUIR_DIVISOR">Excluir del divisor</option>
                          <option value="SMVM_HISTORICO">Completar con SMVM</option>
                          <option value="MANTENER_CERO">Mantener en cero</option>
                        </select>
                      ) : (
                        <span className="text-xs text-emerald-400">Automática (tiene monto)</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {asistidoMode ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-profile-accent">Configuración Avanzada</summary>
              <div className="mt-2 space-y-3">
                <div className="rounded-lg border border-white/15 bg-slate-900/60 p-2">
                  <p className="mb-2 text-xs font-semibold text-slate-200">
                    <Tooltip text={FIELD_TOOLTIPS.estrategia.tooltipText}>Estrategia por fila</Tooltip>
                  </p>
                  {manualRowsDraft.length === 0 ? (
                    <p className="text-xs text-slate-400">Agregá filas para configurar la estrategia de cada período.</p>
                  ) : (
                    manualRowsDraft.map((r, i) => (
                      <div key={`estrategia-${i}`} className="mb-1 flex items-center gap-2">
                        <span className="w-20 text-xs text-slate-300">Fila {i + 1}:</span>
                        {String(r.monto || '').replace(',', '.').replace(/[^0-9.]/g, '') === '' || Number(String(r.monto || '').replace(',', '.')) <= 0 ? (
                          <select value={r.estrategia || 'EXCLUIR_DIVISOR'} onChange={(e) => updateManualRow(i, 'estrategia', e.target.value)} className="h-8 rounded border border-white/20 bg-slate-900/60 px-2 text-xs text-white">
                            <option value="EXCLUIR_DIVISOR">Excluir del divisor</option>
                            <option value="SMVM_HISTORICO">Completar con SMVM</option>
                            <option value="MANTENER_CERO">Mantener en cero</option>
                          </select>
                        ) : (
                          <span className="text-xs text-emerald-400">Automática (tiene monto)</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={addManualRow} className="rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white">Agregar fila</button>
                  <button type="button" disabled={!manualRowsValid} onClick={loadManualRows} className="btn-profile-soft rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Generar tabla para revisión</button>
                </div>
              </div>
            </details>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={addManualRow} className="rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white">Agregar fila</button>
              <button type="button" disabled={!manualRowsValid} onClick={loadManualRows} className="btn-profile-soft rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Generar tabla para revisión</button>
            </div>
          )}
        </div>
      )}

      {showCaseChangeConfirm && (
        <ConfirmModal
          title="Cambio de caso detectado"
          message={`Ya hay datos cargados para otro caso (${showCaseChangeConfirm.currentCaseKey}). ¿Limpiar y cargar el nuevo caso (${showCaseChangeConfirm.incomingCaseKey})?`}
          onConfirm={() => applyProcessedData(showCaseChangeConfirm.d, true)}
          onCancel={() => setShowCaseChangeConfirm(null)}
        />
      )}
    </section>
  )
}
